var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class HallResearchEmcee {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        // other class properties and constructor
        this.mediaInputCode = '1'; // for recording
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`FW`, /^VER\s+v?([\d\.]+)/i, tags);
                return match[1];
            }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value.operation === 'mediaRecorderRecord') {
                    const match = yield this.socket.send(`RC1`, /^(OK|ERROR)/i, tags);
                    if (match[1].toUpperCase() === 'OK') {
                        if (this.state.mediaRecorderState.values[key] !== 'recording') {
                            this.state.mediaRecorderStartTime.update(key, Date.now());
                        }
                        this.state.mediaRecorderState.update(key, 'recording');
                    }
                }
                else if (value.operation === 'mediaRecorderStop') {
                    const match = yield this.socket.send(`RC0`, /^(OK|ERROR)/i, tags);
                    if (match[1].toUpperCase() === 'OK') {
                        this.state.mediaRecorderState.update(key, 'stopped');
                    }
                }
                return value;
            }),
            // Disable control until we support media operations on outputs.
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () { return false; }),
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const code = this.mediaInputCode;
                const media = {};
                media[code] = ['mediaRecorderStop', 'mediaRecorderRecord'];
                return media;
            }),
            // Disable control until we support media operations on outputs.
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () { return false; }),
        };
        this.mediaRecorderStartTime = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                return (_a = this.state.mediaRecorderStartTime.values[key]) !== null && _a !== void 0 ? _a : 0;
            }),
            // Disable control until we support media operations on outputs.
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () { return false; }),
        };
        this.mediaRecorderState = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`RC?`, /^RC(\d)/, tags);
                return match[1] === '1' ? 'recording' : 'stopped';
            }),
            getKeys: () => {
                return [this.mediaInputCode];
            },
            // Disable control until we support media operations on outputs.
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () { return false; }),
            powerOffRestrictions: ['get'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`PW?`, /PW(\d)/i, tags);
                return match[1] === '1';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`PW${power ? '1' : '0'}`, /^(OK|ERROR)/i, tags);
                return match[1].toUpperCase() === 'OK' ? power : this.state.power.value;
            }),
        };
        // For the Emcee device, we recall video layout presets instead of switching inputs to outputs.
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`PR?`, /^PR(\d+)/i, tags);
                return match[1];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`PR${value}`, /^(OK|ERROR)/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return match[1].toUpperCase() === 'OK'
                    ? value
                    : this.state.videoMatrixRoutes.values[key];
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOL?`, /^VOL(\d+)/i, tags);
                return parseInt(match[1], 10);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOL${volume}`, /^(OK|ERROR)/i, tags);
                return match[1].toUpperCase() === 'OK' ? volume : this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        // polling
        this.polls = [
            { intervalMs: 5000, controls: ['power', 'videoMatrixRoutes', 'volume'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.state.busy.update(false);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: `PW?`,
                    next: /PW\d|Welcome/i,
                },
            ], credentials);
        });
    }
}
// static properties
HallResearchEmcee.driverType = 'socketDriver';
HallResearchEmcee.key = '-NMePR4ukVXDKvhH3wh5';
HallResearchEmcee.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r', '\n'],
    },
    sendTerminator: '\r',
};
HallResearchEmcee.socketOptions = [
    Object.assign(Object.assign({}, HallResearchEmcee.baseSocketOptions), { port: 9999 }),
    Object.assign(Object.assign({}, HallResearchEmcee.baseSocketOptions), { port: 4999 }),
];
